import { jsx as _jsx } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import "./WidgetLayout.scss";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import useEmailDomain from "@auth/hooks/useEmailDomain";
import { useIataContext } from "@contexts/IataContext";
import lookup from "../../widgetRegistry";
import Spinner from "../Spinner/Spinner";
import Widget from "../Widget/Widget";
const getGridStyles = (props) => {
    return {
        "--grid-row-mobile": props.gridRow[0],
        "--grid-row-tablet": props.gridRow[1],
        "--grid-row-desktop": props.gridRow[2],
        "--grid-column-mobile": props.gridColumn[0],
        "--grid-column-tablet": props.gridColumn[1],
        "--grid-column-desktop": props.gridColumn[2],
        "--row-span-mobile": props.rowSpan[0],
        "--row-span-tablet": props.rowSpan[1],
        "--row-span-desktop": props.rowSpan[2],
        "--column-span-mobile": props.columnSpan[0],
        "--column-span-tablet": props.columnSpan[1],
        "--column-span-desktop": props.columnSpan[2],
        "--min-height": `${props.componentProps.minHeight ? props.componentProps.minHeight : 0}px`,
    };
};
export const WidgetLayout = ({ widgets, layout }) => {
    const { domain } = useEmailDomain();
    const { iataLowerCase } = useIataContext();
    // Filter widgets based on domain permissions
    // Clone widgets to avoid mutating the original array
    const clonedWidgets = widgets.map((widget) => JSON.parse(JSON.stringify(Object.assign({}, widget))));
    const filteredWidgets = clonedWidgets.filter((w) => {
        var _a, _b;
        const hasPermission = !((_a = w.permissions) === null || _a === void 0 ? void 0 : _a.requiredDomain) || w.permissions.requiredDomain === domain;
        const matchesIata = !((_b = w.showOnlyForIatas) === null || _b === void 0 ? void 0 : _b.length) || w.showOnlyForIatas.some((x) => x.toLowerCase() === iataLowerCase);
        return hasPermission && matchesIata;
    });
    // Adjust grid positions based on removed widgets
    const adjustGridPositions = (widgetsToAdjust, removedWidgets) => {
        const breakpoints = [0, 1, 2]; // mobile, tablet, desktop
        breakpoints.forEach((bp) => {
            const visibleByRow = new Map();
            const removedByRow = new Map();
            widgetsToAdjust.forEach((w) => {
                const row = w.gridRow[bp];
                if (!visibleByRow.has(row))
                    visibleByRow.set(row, []);
                visibleByRow.get(row).push(w);
            });
            removedWidgets.forEach((w) => {
                const row = w.gridRow[bp];
                if (!removedByRow.has(row))
                    removedByRow.set(row, []);
                removedByRow.get(row).push(w);
            });
            // Get all unique rows from both maps
            const allRows = new Set([...visibleByRow.keys(), ...removedByRow.keys()]);
            const sortedRows = [...allRows].sort((a, b) => a - b);
            let offset = 0;
            for (const row of sortedRows) {
                const visibleWidgets = visibleByRow.get(row) || [];
                const removedWidgetsInRow = removedByRow.get(row) || [];
                const allRemoved = visibleWidgets.length === 0 && removedWidgetsInRow.length > 0;
                const someRemoved = visibleWidgets.length > 0 && removedWidgetsInRow.length > 0;
                // 1. Shift rows if needed
                if (offset > 0) {
                    visibleWidgets.forEach((w) => {
                        w.gridRow[bp] -= offset;
                    });
                }
                // 2. Reflow columns if partial removal
                if (someRemoved) {
                    visibleWidgets
                        .sort((a, b) => a.gridColumn[bp] - b.gridColumn[bp])
                        .reduce((col, w) => {
                        w.gridColumn[bp] = col;
                        return col + w.columnSpan[bp];
                    }, 1);
                }
                // 3. If full row is removed, update offset
                if (allRemoved) {
                    const rowSpan = Math.max(...removedWidgetsInRow.map((w) => w.rowSpan[bp]));
                    offset += rowSpan || 1;
                }
            }
        });
    };
    const removedWidgets = clonedWidgets.filter((w) => !filteredWidgets.includes(w));
    adjustGridPositions(filteredWidgets, removedWidgets);
    const widgetComponents = filteredWidgets.map((widget, i) => {
        return (_createElement(WidgetComponent, Object.assign({}, widget, { key: `${widget.name}-${i}` })));
    });
    return (_jsx("section", { style: layout, className: "widget-layout", children: widgetComponents }));
};
const WidgetComponent = (props) => {
    const Component = lookup(props.component) || (() => _jsx("div", { children: "Component not found" }));
    const { t } = useTranslation();
    const widgetStyle = getGridStyles(props);
    return (_jsx(Widget, { style: widgetStyle, className: `widget ${props.name}`, children: _jsx(Suspense, { fallback: _jsx(Spinner, {}), children: _jsx(Component, Object.assign({}, props.componentProps, props, { t: t })) }) }, props.name));
};
